import type { JSX } from 'react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Input, Modal, Select, Skeleton, message } from 'antd';
import {
  BookOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  EnterOutlined,
  DislikeOutlined,
  FileTextOutlined,
  FireOutlined,
  FolderOpenOutlined,
  LinkOutlined,
  LikeOutlined,
  SafetyCertificateOutlined,
  ScheduleOutlined,
  SearchOutlined,
  SendOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

import { aiApi, editorApi, wikiApi, type AiMaterialRef, type AiClassifyDto } from '../lib/wikiApi';
import { authApi } from '../lib/authApi';
import { kbApi } from '../lib/api/kb';
import { AiChecksTab } from '../components/AiChecksTab';
import { Button as DsButton, Chip } from '../components/ds';
import './KnowledgeBase.css';

/**
 * AI-помощник (HUB 2.0, прототип «ai»). Слева — чат: вопрос → POST
 * /wiki/ai/ask (RAG по базе знаний с ACL вьюера), ответ с источниками-ссылками
 * и оценкой «Помогло / Не то» (POST /wiki/ai/feedback → журнал событий).
 * Справа — AI-действия: пересказ статьи, тест по статье и разнесение по
 * разделам (kb:create), дубли и актуальность (kb:admin, вкладка AI-проверок),
 * «Часто спрашивают» (реальные частые запросы) и недавние запросы.
 */

const POPULAR = [
  'Как открыть точку?',
  'Какие стандарты подтвердить?',
  'Как приготовить рис для роллов?',
  'Где взять макеты акции?',
];

interface ChatMsg {
  who: 'user' | 'ai';
  text: string;
  /** Для AI-сообщений: найденные материалы (карточки-ссылки). */
  materials?: AiMaterialRef[];
  /** Вопрос, на который это ответ (для «Помогло / Не то»). */
  query?: string;
  /** Оценка ответа: true — помогло, false — не то. */
  rated?: boolean;
}

const AI_INTRO =
  'Я помогу найти инструкцию, стандарт или документ в базе знаний Fishka Sushi. Спросите своими словами — подберу подходящие материалы.';

/** Какая операция открыта в панели действий (модалке). */
type ActionKind = 'summarize' | 'generate-test' | 'classify' | 'checks';

export function AiAssistantPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [input, setInput] = useState('');
  const [recent, setRecent] = useState<string[]>([]);
  // История диалога. Стартовое приветствие AI.
  const [messages, setMessages] = useState<ChatMsg[]>([{ who: 'ai', text: AI_INTRO }]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Какой ?q уже обработали — чтобы не задвоить запрос при ререндере.
  const handledQ = useRef<string | null>(null);
  // Какая AI-операция открыта (модалка действия) — null если закрыто.
  const [action, setAction] = useState<ActionKind | null>(null);

  // Права вошедшего — гейтят действия, требующие редактора/админа.
  const me = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const canCreate = (me.data?.canCreate ?? false) || (me.data?.canAdmin ?? false);
  const canAdmin = me.data?.canAdmin ?? false;

  // «Популярные вопросы» — реальные частые запросы (журнал поиска), иначе примеры.
  const popularQ = useQuery({ queryKey: ['wiki', 'search', 'popular'], queryFn: () => kbApi.popularQueries(6), retry: false, staleTime: 10 * 60_000 });
  const popular = (popularQ.data ?? []).length >= 3 ? (popularQ.data ?? []).map((p) => p.query) : POPULAR;

  // «Помогло / Не то» → журнал событий (verb=rated, objectType=ai).
  const rate = (idx: number, helpful: boolean): void => {
    const msg = messages[idx];
    if (!msg || msg.who !== 'ai' || !msg.query || msg.rated !== undefined) return;
    setMessages((m) => m.map((x, i) => (i === idx ? { ...x, rated: helpful } : x)));
    kbApi
      .aiFeedback({ helpful, query: msg.query, sources: (msg.materials ?? []).map((a) => a.slug) })
      .then(
        () => message.success(helpful ? 'Спасибо! Учтём, что ответ помог' : 'Спасибо — передадим УК, чтобы дополнить базу'),
        () => undefined,
      );
  };

  // Запрос к AI-ассистенту: вопрос → ответ LLM + найденные материалы.
  const askMut = useMutation({
    mutationFn: (query: string) => aiApi.ask(query),
    onSuccess: (res, query) => {
      setMessages((m) => [...m, { who: 'ai', text: res.answer, materials: res.materials, query }]);
    },
    onError: (e: Error) => {
      message.error(e.message || 'Не удалось получить ответ');
      setMessages((m) => [
        ...m,
        { who: 'ai', text: 'Не удалось получить ответ прямо сейчас. Попробуйте ещё раз чуть позже.' },
      ]);
    },
  });

  const ask = (text: string): void => {
    const q = text.trim();
    if (q.length < 2 || askMut.isPending) return;
    setInput('');
    setParams(q ? { q } : {});
    handledQ.current = q;
    setMessages((m) => [...m, { who: 'user', text: q }]);
    setRecent((r) => [q, ...r.filter((x) => x !== q)].slice(0, 4));
    askMut.mutate(q);
  };

  // Резюме статьи (summarize) приходит из модалки → кладём как ответ AI в чат.
  const pushSummaryToChat = (title: string, summary: string): void => {
    setMessages((m) => [
      ...m,
      { who: 'user', text: `Кратко перескажи статью «${title}»` },
      { who: 'ai', text: summary },
    ]);
    setAction(null);
  };

  // Синхронизация ?q из URL (переход из шапки с вопросом или восстановление).
  useEffect(() => {
    const urlQ = (params.get('q') ?? '').trim();
    if (urlQ && urlQ !== handledQ.current && !askMut.isPending) {
      handledQ.current = urlQ;
      setMessages((m) => [...m, { who: 'user', text: urlQ }]);
      setRecent((r) => [urlQ, ...r.filter((x) => x !== urlQ)].slice(0, 4));
      askMut.mutate(urlQ);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Автоскролл вниз при новых сообщениях / печати.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, askMut.isPending]);

  // Индекс последнего AI-сообщения — к нему крепим карточки материалов.
  const lastAiIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].who === 'ai') return i;
    return -1;
  })();
  // Материалы последнего ответа — для правого рейла «Связанные материалы».
  const lastMaterials = lastAiIdx >= 0 ? messages[lastAiIdx].materials ?? [] : [];

  // ── AI-действия (плитки) ──────────────────────────────────────────
  // Каждая плитка либо открывает рабочую операцию (модалку с реальным
  // эндпоинтом), либо честно помечена «скоро» (нет бэка — не делаем no-op).
  type Tile = {
    icon: JSX.Element;
    label: string;
    hint: string;
    onClick: () => void;
    /** Скрыть плитку, если у юзера нет прав на операцию. */
    hidden?: boolean;
  };
  const tiles: Tile[] = [
    {
      icon: <SearchOutlined />,
      label: 'Найти инструкцию',
      hint: 'Спросите своими словами',
      onClick: () => {
        inputRef.current?.focus();
        inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    },
    { icon: <FileTextOutlined />, label: 'Пересказать статью', hint: 'Коротко, в 3–5 предложениях', onClick: () => setAction('summarize') },
    { icon: <ScheduleOutlined />, label: 'Тест по статье', hint: 'Черновик вопросов для курса', onClick: () => setAction('generate-test'), hidden: !canCreate },
    { icon: <FolderOpenOutlined />, label: 'Разнести по разделам', hint: 'Блоки, категория, теги для текста', onClick: () => setAction('classify'), hidden: !canCreate },
    { icon: <CopyOutlined />, label: 'Найти дубли', hint: 'Похожие статьи в базе', onClick: () => setAction('checks'), hidden: !canAdmin },
    { icon: <SafetyCertificateOutlined />, label: 'Проверить актуальность', hint: 'Просрочки и противоречия стандартов', onClick: () => setAction('checks'), hidden: !canAdmin },
  ];

  return (
    <div className="fk-kb-ai-grid">
      <section className="fk-kb-panel fk-kb-ai-chat">
        <div className="fk-kb-ai-head">
          <span className="fk-kb-ai-badge">
            <ThunderboltOutlined />
          </span>
          <div>
            <h1 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>AI-помощник</h1>
            <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>отвечает только по базе знаний fishka и всегда даёт ссылки на источники</div>
          </div>
        </div>

        <div ref={scrollRef} className="fk-kb-ai-msgs fk-scroll" style={{ maxHeight: 'calc(100vh - 260px)' }}>
          {messages.map((m, i) =>
            m.who === 'user' ? (
              <div key={i} className="fk-kb-ai-user">
                {m.text}
              </div>
            ) : (
              <div key={i} className="fk-kb-ai-bot">
                <div className="fk-kb-ai-bubble">{m.text}</div>
                {m.materials && m.materials.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {m.materials.slice(0, 6).map((a) => (
                      <Link key={a.slug} to={`/article/${a.slug}`} className="fk-kb-ai-src">
                        <BookOutlined /> {a.title}
                      </Link>
                    ))}
                  </div>
                )}
                {m.materials && m.materials.length === 0 && (
                  <div style={{ fontSize: 13, color: 'var(--fk-text-2)' }}>
                    Подходящих материалов в базе не нашлось.{' '}
                    <Link to={`/knowledge/ask?q=${encodeURIComponent(m.query ?? '')}`} style={{ fontWeight: 600 }}>
                      Задать вопрос УК →
                    </Link>
                  </div>
                )}
                {m.query && (
                  <div className="fk-kb-ai-fb">
                    <button type="button" aria-pressed={m.rated === true} disabled={m.rated !== undefined} onClick={() => rate(i, true)}>
                      <LikeOutlined /> Помогло
                    </button>
                    <button type="button" aria-pressed={m.rated === false} disabled={m.rated !== undefined} onClick={() => rate(i, false)}>
                      <DislikeOutlined /> Не то
                    </button>
                    {m.rated === false && (
                      <Link to={`/knowledge/ask?q=${encodeURIComponent(m.query)}`} style={{ fontWeight: 600, fontSize: 12 }}>
                        Задать вопрос УК →
                      </Link>
                    )}
                  </div>
                )}
              </div>
            ),
          )}
          {askMut.isPending && <div className="fk-kb-ai-bubble" style={{ alignSelf: 'flex-start', color: 'var(--fk-text-muted)', fontSize: 13 }}>Ищу в базе знаний…</div>}
        </div>

        <form
          className="fk-kb-ai-input"
          onSubmit={(e) => {
            e.preventDefault();
            ask(input);
          }}
        >
          <input
            ref={inputRef}
            autoFocus
            className="fk-kb-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Спросите: как закрыть смену, срок хранения риса, что делать при жалобе…"
            aria-label="Вопрос AI-помощнику"
          />
          <DsButton type="submit" variant="primary" icon={<SendOutlined />} loading={askMut.isPending}>
            <span className="fk-hide-mobile">Спросить</span>
          </DsButton>
        </form>
      </section>

      <aside style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <section className="fk-kb-panel fk-kb-panel-pad">
          <div style={{ fontWeight: 700, marginBottom: 10 }}>AI-действия</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {tiles
              .filter((t) => !t.hidden)
              .map((t) => (
                <button key={t.label} type="button" className="fk-kb-action" onClick={t.onClick}>
                  <span className="fk-kb-action-ic">{t.icon}</span>
                  <span>
                    <span style={{ display: 'block', fontWeight: 600 }}>{t.label}</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--fk-text-muted)' }}>{t.hint}</span>
                  </span>
                </button>
              ))}
          </div>
        </section>

        <section className="fk-kb-panel fk-kb-panel-pad">
          <div style={{ fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
            <FireOutlined style={{ color: 'var(--fk-orange)' }} /> Часто спрашивают
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {popular.map((q) => (
              <Chip key={q} onClick={() => ask(q)}>
                {q}
              </Chip>
            ))}
          </div>
        </section>

        {recent.length > 0 && (
          <section className="fk-kb-panel fk-kb-panel-pad">
            <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 7 }}>
              <ClockCircleOutlined style={{ color: 'var(--fk-text-muted)' }} /> Недавние запросы
            </div>
            {recent.map((q) => (
              <button key={q} type="button" onClick={() => ask(q)} className="fk-kb-link-row" style={{ width: '100%', background: 'none', border: 'none', borderBottom: '1px solid var(--fk-row-line)', textAlign: 'left', cursor: 'pointer' }}>
                <span className="fk-kb-link-title" style={{ fontSize: 13 }}>
                  <EnterOutlined style={{ color: 'var(--fk-text-muted)', transform: 'scaleX(-1)', marginRight: 6 }} />
                  {q}
                </span>
              </button>
            ))}
          </section>
        )}

        {lastMaterials.length > 0 && (
          <section className="fk-kb-panel fk-kb-panel-pad">
            <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 7 }}>
              <LinkOutlined style={{ color: 'var(--fk-orange)' }} /> Связанные материалы
            </div>
            {lastMaterials.slice(0, 5).map((a) => (
              <Link key={a.slug} to={`/article/${a.slug}`} className="fk-kb-link-row">
                <span className="fk-kb-link-title" style={{ fontSize: 13 }}>{a.title}</span>
              </Link>
            ))}
          </section>
        )}
      </aside>

      {action === 'summarize' && <SummarizeModal onClose={() => setAction(null)} onInsert={pushSummaryToChat} />}
      {action === 'generate-test' && <GenerateTestModal onClose={() => setAction(null)} onOpenBuilder={() => navigate('/admin/courses')} />}
      {action === 'classify' && <ClassifyModal onClose={() => setAction(null)} />}
      {action === 'checks' && (
        <Modal open title="AI-проверки базы знаний" onCancel={() => setAction(null)} footer={null} width={820} destroyOnHidden>
          <AiChecksTab />
        </Modal>
      )}
    </div>
  );
}

/**
 * Пикер статьи через FTS-автокомплит (как в конструкторе курсов). Возвращает
 * выбранный slug + заголовок наверх. Поиск стартует с 2 символов.
 */
function ArticlePicker({
  value,
  onChange,
  placeholder,
}: {
  value?: string;
  onChange: (slug: string | undefined, title: string | undefined) => void;
  placeholder: string;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const suggest = useQuery({
    queryKey: ['wiki', 'search', 'suggest', search],
    queryFn: () => wikiApi.searchSuggest(search),
    enabled: search.trim().length >= 2,
  });
  const items = suggest.data?.items ?? [];
  return (
    <Select
      showSearch
      value={value}
      onSearch={setSearch}
      filterOption={false}
      placeholder={placeholder}
      style={{ width: '100%' }}
      notFoundContent={suggest.isLoading ? 'Поиск…' : search.trim().length < 2 ? 'Введите 2+ символа' : 'Ничего не найдено'}
      onChange={(slug) => onChange(slug, items.find((a) => a.slug === slug)?.title)}
      options={items.map((a) => ({ value: a.slug, label: a.title }))}
    />
  );
}

/**
 * «Суммаризировать статью»: выбор статьи → POST /wiki/ai/summarize → резюме.
 * Кнопка «Вставить в чат» добавляет резюме в диалог как ответ AI.
 */
function SummarizeModal({
  onClose,
  onInsert,
}: {
  onClose: () => void;
  onInsert: (title: string, summary: string) => void;
}): JSX.Element {
  const [slug, setSlug] = useState<string | undefined>();
  const sum = useMutation({
    mutationFn: () => {
      if (!slug) throw new Error('Выберите статью');
      return aiApi.summarize(slug);
    },
    onError: (e: Error) => message.error(e.message),
  });
  const result = sum.data;
  return (
    <Modal open title="Суммаризировать статью" onCancel={onClose} footer={null} width={620} destroyOnHidden>
      <p style={{ fontSize: 13, color: 'var(--fk-text-muted)', marginTop: 0 }}>
        Выберите статью базы знаний — AI сделает краткий пересказ в 3-5 предложений.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <ArticlePicker
            value={slug}
            placeholder="Найти статью…"
            onChange={(s) => { setSlug(s); sum.reset(); }}
          />
        </div>
        <button type="button" className="fk-btn fk-btn-primary" disabled={!slug || sum.isPending} onClick={() => sum.mutate()}>
          {sum.isPending ? 'Готовлю…' : 'Пересказать'}
        </button>
      </div>

      {sum.isPending && <Skeleton active paragraph={{ rows: 3 }} />}
      {result && (
        <div style={{ border: '1px solid var(--fk-border)', borderRadius: 10, padding: 16, background: 'var(--fk-bg-subtle)' }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 7 }}>
            <FileTextOutlined style={{ color: 'var(--fk-orange)' }} /> {result.title}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{result.summary}</div>
          {!result.aiUsed && (
            <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginTop: 10 }}>
              AI-пересказ недоступен (провайдер не настроен или статья слишком короткая) — показано краткое описание статьи.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <button type="button" className="fk-btn fk-btn-primary" onClick={() => onInsert(result.title, result.summary)}>
              Вставить в чат
            </button>
            <Link to={`/article/${result.slug}`} className="fk-btn fk-btn-ghost" onClick={onClose}>
              Открыть статью
            </Link>
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * «Создать тест по статье»: выбор статьи → POST /wiki/ai/generate-test →
 * draft-вопросы (read-only) + переход в конструктор курсов. Сами вопросы
 * не сохраняются здесь — их создаёт конструктор (kb:create|kb:admin).
 */
function GenerateTestModal({
  onClose,
  onOpenBuilder,
}: {
  onClose: () => void;
  onOpenBuilder: () => void;
}): JSX.Element {
  const [slug, setSlug] = useState<string | undefined>();
  const gen = useMutation({
    mutationFn: () => {
      if (!slug) throw new Error('Выберите статью');
      return aiApi.generateTest(slug);
    },
    onError: (e: Error) => message.error(e.message),
  });
  const result = gen.data;
  return (
    <Modal open title="Создать тест по статье" onCancel={onClose} footer={null} width={680} destroyOnHidden>
      <p style={{ fontSize: 13, color: 'var(--fk-text-muted)', marginTop: 0 }}>
        AI составит 3-5 проверочных вопросов по содержанию статьи. Это предпросмотр —
        сохранить тест и привязать к курсу можно в конструкторе курсов.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <ArticlePicker
            value={slug}
            placeholder="Статья-источник…"
            onChange={(s) => { setSlug(s); gen.reset(); }}
          />
        </div>
        <button type="button" className="fk-btn fk-btn-primary" disabled={!slug || gen.isPending} onClick={() => gen.mutate()}>
          {gen.isPending ? 'Генерация…' : 'Сгенерировать'}
        </button>
      </div>

      {gen.isPending && <Skeleton active paragraph={{ rows: 5 }} />}
      {result && result.questions.length === 0 && (
        <div style={{ fontSize: 13.5, color: 'var(--fk-text-2)', padding: '8px 0' }}>
          {result.aiUsed
            ? 'AI не смог составить вопросы по этой статье — попробуйте другую.'
            : 'AI недоступен (провайдер не настроен) или статья слишком короткая для теста.'}
        </div>
      )}
      {result && result.questions.length > 0 && (
        <>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--fk-text-muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 10 }}>
            Предпросмотр · {result.questions.length} вопрос(ов)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 360, overflowY: 'auto' }} className="fk-scroll">
            {result.questions.map((q, i) => (
              <div key={i} style={{ border: '1px solid var(--fk-border)', borderRadius: 10, padding: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{i + 1}. {q.text}</div>
                {q.options.map((o, j) => (
                  <div key={j} style={{ fontSize: 13, color: o.isCorrect ? 'var(--fk-success)' : 'var(--fk-text-2)' }}>
                    {o.isCorrect ? '✓' : '○'} {o.text}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" className="fk-btn fk-btn-primary" onClick={onOpenBuilder}>
              Открыть в конструкторе курсов
            </button>
            <span style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>
              В конструкторе выберите ту же статью и нажмите «Сгенерировать», чтобы сохранить вопросы в тест.
            </span>
          </div>
        </>
      )}
    </Modal>
  );
}

/**
 * «Разнести текст по категориям» (classify): произвольный текст (+опц.
 * заголовок) → POST /wiki/ai/classify → предложенные блоки/категория/теги/
 * excerpt. Это та же AI-классификация, что в редакторе статей, но по
 * вставленному тексту. Только предпросмотр (ничего не сохраняет).
 */
function ClassifyModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const cls = useMutation({
    mutationFn: async (): Promise<AiClassifyDto> => {
      const text = body.trim();
      if (text.length < 40) throw new Error('Вставьте хотя бы пару абзацев текста (от 40 символов)');
      const res = await editorApi.aiClassify({ title: title.trim() || 'Без названия', bodyMd: text });
      if ('error' in res) throw new Error(res.error || 'AI-классификация недоступна');
      return res;
    },
    onError: (e: Error) => message.error(e.message),
  });
  const r = cls.data;
  return (
    <Modal open title="Разнести текст по категориям" onCancel={onClose} footer={null} width={620} destroyOnHidden>
      <p style={{ fontSize: 13, color: 'var(--fk-text-muted)', marginTop: 0 }}>
        Вставьте текст статьи или документа — AI предложит подходящие блоки базы знаний,
        категорию, теги и краткое описание.
      </p>
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Заголовок (необязательно)"
        style={{ marginBottom: 8 }}
      />
      <Input.TextArea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Вставьте текст…"
        autoSize={{ minRows: 6, maxRows: 12 }}
        style={{ marginBottom: 12 }}
      />
      <button type="button" className="fk-btn fk-btn-primary" disabled={cls.isPending} onClick={() => cls.mutate()}>
        {cls.isPending ? 'Анализирую…' : 'Разнести по категориям'}
      </button>

      {cls.isPending && <div style={{ marginTop: 14 }}><Skeleton active paragraph={{ rows: 3 }} /></div>}
      {r && (
        <div style={{ marginTop: 14, border: '1px solid var(--fk-border)', borderRadius: 10, padding: 16, background: 'var(--fk-bg-subtle)' }}>
          <Field label="Блоки">{r.blocks.length ? r.blocks.join(', ') : '—'}</Field>
          <Field label="Категория">{r.category ?? '—'}</Field>
          <Field label="Теги">{r.tags.length ? r.tags.join(', ') : '—'}</Field>
          {r.suggestedTitle && <Field label="Предложенный заголовок">{r.suggestedTitle}</Field>}
          {r.excerpt && <Field label="Краткое описание">{r.excerpt}</Field>}
          <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginTop: 8 }}>
            Уверенность AI: {r.confidence}% · применить классификацию можно при создании статьи в редакторе.
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Строка «метка: значение» для результата классификации. */
function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 8, fontSize: 13.5 }}>
      <span style={{ fontWeight: 700, color: 'var(--fk-text-muted)', minWidth: 150 }}>{label}</span>
      <span style={{ flex: 1 }}>{children}</span>
    </div>
  );
}
